import React, { useState, useRef } from "react";
import MaterialTable from "material-table";
import ViewListOutlinedIcon from "@material-ui/icons/ViewListOutlined";
import * as XLSX from "xlsx";
import GetAppIcon from "@material-ui/icons/GetApp";
import axios from "axios";
import Popup from "./Popup";
import {
  Grid,
  makeStyles,
  Select,
  TextField,
  Button,
  Typography,
  InputLabel,
  FormLabel,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormGroup,
  Checkbox,
  MenuItem,
} from "@material-ui/core";

import { Receipt } from "@material-ui/icons";
import {
  createNetInspectionApiClient,
  getInspectorList,
  getPaymentTypeList,
  getPaymentStatusList,
  getAdminInspectionsApiUrl,
  getAdminInspectionsPaymentLogApiUrl,
  getAdminInspectionsReconcileApiUrl,
  getAdminInspectionsUnreconcileApiUrl,
  getAdminEditPaymentApiUrl,
} from "../services";
import LoadingIndicator from "./LoadingIndicator";
import ReconcileSubmit from "./ReconcileSubmit";
import UnreconcileSubmit from "./UnreconcileSubmit";
import ViewReconcileHistory from "./ViewReconcileHistory";
import EditPayment from "./EditPayment";
import ViewPaymentLog from "./ViewPaymentLog";
import { MaterialTableProps } from "material-table";
import { TablePagination, TablePaginationProps } from "@material-ui/core";

// for reading user roles
import {
  selectUser,
  selectIsUserAdmin,
  selectIsUserInspector,
  selectIsUserAdminEdit,
} from "selectors/user";
import { useDispatch, useSelector } from "react-redux";

import { useSnackbar } from "notistack";

function PatchedPagination(props) {
  const {
    ActionsComponent,
    onChangePage,
    onChangeRowsPerPage,
    ...tablePaginationProps
  } = props;

  return (
    <TablePagination
      {...tablePaginationProps}
      // @ts-expect-error onChangePage was renamed to onPageChange
      onPageChange={onChangePage}
      onRowsPerPageChange={onChangeRowsPerPage}
      ActionsComponent={(subprops) => {
        const { onPageChange, ...actionsComponentProps } = subprops;
        return (
          // @ts-expect-error ActionsComponent is provided by material-table
          <ActionsComponent
            {...actionsComponentProps}
            onChangePage={onPageChange}
          />
        );
      }}
    />
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  labelHeight: {
    fontSize: "0.7rem",
    padding: `${theme.spacing(1)}px 0px`,
  },

  dateTextField: {
    width: 100,
    margin: 0,
    "& .MuiInputBase-root": {
      padding: `0px ${theme.spacing(1)}px`,
      fontSize: "0.7rem",
    },
    "& .MuiOutlinedInput-inputMarginDense": {
      padding: `0px ${theme.spacing(1)}px`,
    },
  },

  textField: {
    width: 200,
    margin: 0,
    "& .MuiInputBase-root": {
      padding: `4px ${theme.spacing(0)}px`,
      fontSize: "1rem",
    },
    "& .MuiOutlinedInput-inputMarginDense": {
      padding: `0px ${theme.spacing(1)}px`,
    },
  },
  headerStyle: {
    marginLeft: "8px",
    margingBottom: "4px",
    marginTop: "4px",
    // backgroundColor: "#e3f2fd",
    backgroundColor: "#e2efd9",
  },

  selectInBlock: {
    display: "inline-block",
    padding: theme.spacing(1),
  },

  checkBox: {
    display: "inline-block",
    padding: theme.spacing(1),
    "& .MuiIconButtonBase-root": {
      padding: "0px",
    },
    "& .MuiIconButton-label": {
      color: theme.palette.primary.light,
    },
    "& .MuiTypography-body1": {
      fontSize: "0.8rem",
    },
  },
}));

const getCurrentDateAsYYYYMMDD = () => {
  const dt = new Date();
  let formattedDt =
    dt.getFullYear().toString() +
    "-" +
    (dt.getMonth() + 1).toString().padStart(2, "0") +
    "-" +
    dt.getDate().toString().padStart(2, "0");
  return formattedDt;
};

const initialFieldValues = {
  inspectionStartDate: getCurrentDateAsYYYYMMDD(),
  inspectionEndDate: getCurrentDateAsYYYYMMDD(),
  inspector: "",
  certificateNumber: "*",
  ownerName: "*",
  buyerName: "*",
  payerName: "*",
  paymentStatus: "all",
  paymentType: "all",
  checkNumber: "*",
  reconcileStatus: "unreconciled",
  reconcileStartingDate: "",
  reconcileEndingDate: "",
};

function getInitialDisplayValues() {
  let savedColumns = JSON.parse(
    window.localStorage.getItem("adminInspectionsTableColumnsBrandsClient")
  );

  let displayOwnerCheckboxChecked = true;
  let displayBuyerCheckboxChecked = true;
  let displayPayerCheckboxChecked = true;

  if (savedColumns) {
    let displayOwnerColumn = savedColumns.find(
      (col) => col.field == "owner_name"
    );
    let displayBuyerColumn = savedColumns.find(
      (col) => col.field == "buyer_name"
    );
    let displayPayerColumn = savedColumns.find(
      (col) => col.field == "payer_name"
    );

    if (!displayOwnerColumn) {
      displayOwnerCheckboxChecked = false;
    }
    if (!displayBuyerColumn) {
      displayBuyerCheckboxChecked = false;
    }
    if (!displayPayerColumn) {
      displayPayerCheckboxChecked = false;
    }
  }
  return {
    displayOwnerCheckboxChecked: displayOwnerCheckboxChecked,
    displayBuyerCheckboxChecked: displayBuyerCheckboxChecked,
    displayPayerCheckboxChecked: displayPayerCheckboxChecked,
  };
}

const SearchInspections = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const isUserInspector = useSelector(selectIsUserInspector);
  const isUserAdminEdit = useSelector(selectIsUserAdminEdit);
  const isUserAdmin = useSelector(selectIsUserAdmin);
  const user = useSelector(selectUser);

  const [searchFieldsData, setSearchFieldsData] = useState(initialFieldValues);
  const MIN_DATE = new Date(2022, 0); // Month is 0 based
  const [inspectionData, setInspectionData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openPopupReconcileSubmit, setOpenPopupReconcileSubmit] =
    useState(false);
  const [openPopupUnreconcileSubmit, setOpenPopupUnreconcileSubmit] =
    useState(false);
  const [openPopupReconcileHistory, setOpenPopupReconcileHistory] =
    useState(false);

  const [openPopupViewPaymentLog, setOpenPopupViewPaymentLog] = useState(false);
  const [openPopupEditPayment, setOpenPopupEditPayment] = useState(false);

  const [rowDataForReconcileHistory, setRowDataForReconcileHistory] = useState(
    {}
  );

  const [paymentIdForViewPaymentLog, setPaymentIdForViewPaymentLog] =
    useState(null);
  const [disableEditPaymentButton, setDisableEditPaymentButton] =
    useState(true);

  const [disableReconcileButton, setDisableReconcileButton] = useState(true);

  const [disableUnreconcileButton, setDisableUnreconcileButton] =
    useState(true);

  const inspectionDataURL = getAdminInspectionsApiUrl();

  const displayOptions = getInitialDisplayValues();

  const [displayOwnerName, setDisplayOwnerName] = useState(
    displayOptions.displayOwnerCheckboxChecked
  );
  const [displayBuyerName, setDisplayBuyerName] = useState(
    displayOptions.displayBuyerCheckboxChecked
  );
  const [displayPayerName, setDisplayPayerName] = useState(
    displayOptions.displayPayerCheckboxChecked
  );

  const [tableColumns, setTableColumns] = useState(handleColumnPersistence());

  const handleDisplayOptionsCheckBoxChange = (event) => {
    let name = event.target.name;
    let checked = event.target.checked;
    console.log("name:checked = ", name, checked);

    if (name == "displayOwnerName") {
      setDisplayOwnerName(checked);
    }
    if (name == "displayBuyerName") {
      setDisplayBuyerName(checked);
    }
    if (name == "displayPayerName") {
      setDisplayPayerName(checked);
    }
  };

  const handleDisplayOptionsChange = (event) => {
    handleDisplayOptionsCheckBoxChange(event);

    // adding below code to handle adding/ removing columns
    // this is because changing column order by dragging/ dropping past hidden columns
    // not working; it may be a bug in the material-table
    // as a work around we are removing the hidden columns and only keeping visibile columns

    let tempColumns = [];
    let tempSavedColumns = [];
    let savedColumns = JSON.parse(
      window.localStorage.getItem("adminInspectionsTableColumnsBrandsClient")
    );

    if (savedColumns) {
      // first get elements into tempSavedColumns
      tempSavedColumns = [...savedColumns];
      if (event.target.name == "displayOwnerName") {
        let ownerColumn = tempSavedColumns.find(
          (item) => item?.field == "owner_name"
        );

        if (event.target.checked && ownerColumn == undefined) {
          tempSavedColumns.push({
            title: "Owner Name",
            field: "owner_name",
            searchable: true,
            hidden: false,
            headerStyle: {
              whiteSpace: "nowrap",
              padding: "10px",
              // backgroundColor: "#fff2cc",
              backgroundColor: "#edd2af",
            },
            cellStyle: {
              whiteSpace: "nowrap",
              padding: "10px",
              verticalAlign: "top",
            },
          });
        }
        if (!event.target.checked && ownerColumn !== undefined) {
          // if not checked and ownerColumn is not null then we need to remove it
          tempSavedColumns = tempSavedColumns.map((item) => {
            if (item.field == "owner_name") {
              // nothing
            } else {
              return item;
            }
          });
        }
      }

      if (event.target.name == "displayBuyerName") {
        let buyerColumn = tempSavedColumns.find(
          (item) => item?.field == "buyer_name"
        );

        if (event.target.checked && buyerColumn == undefined) {
          tempSavedColumns.push({
            title: "Buyer Name",
            field: "buyer_name",
            hidden: false,
            searchable: true,
            headerStyle: {
              whiteSpace: "nowrap",
              padding: "10px",
              // backgroundColor: "#fff2cc",
              backgroundColor: "#edd2af",
            },
            cellStyle: {
              whiteSpace: "nowrap",
              padding: "10px",
              verticalAlign: "top",
            },
          });
        }
        if (!event.target.checked && buyerColumn != undefined) {
          // if not checked and ownerColumn is not null then we need to remove it
          tempSavedColumns = tempSavedColumns.map((item) => {
            if (item.field == "buyer_name") {
              // nothing
            } else {
              return item;
            }
          });
        }
      }

      if (event.target.name == "displayPayerName") {
        let payerColumn = tempSavedColumns.find(
          (item) => item?.field == "payer_name"
        );

        if (event.target.checked && payerColumn == undefined) {
          tempSavedColumns.push({
            title: "Payer Name",
            field: "payer_name",
            hidden: false,
            searchable: true,
            headerStyle: {
              whiteSpace: "nowrap",
              padding: "10px",
              // backgroundColor: "#fff2cc",
              backgroundColor: "#edd2af",
            },
            cellStyle: {
              whiteSpace: "nowrap",
              padding: "10px",
              verticalAlign: "top",
            },
          });
        }
        if (!event.target.checked && payerColumn !== undefined) {
          // if not checked and ownerColumn is not null then we need to remove it
          tempSavedColumns = tempSavedColumns.map((item) => {
            if (item.field == "payer_name") {
              // nothing
            } else {
              return item;
            }
          });
        }
      }

      for (var item in tempSavedColumns) {
        if (tempSavedColumns[item] != null) {
          tempColumns.push(tempSavedColumns[item]);
        }
      }

      window.localStorage.setItem(
        "adminInspectionsTableColumnsBrandsClient",
        JSON.stringify(tempColumns)
      );

      setTableColumns(tempColumns);
    }
  };

  const handleInputChange = (e) => {
    // making sure inspectors are searching only their records
    if (!isUserAdmin && !isUserAdminEdit && isUserInspector) {
      const { name, value } = e.target;
      if (name === "inspector") {
        setSearchFieldsData({
          ...searchFieldsData,
          inspector: user.name,
        });
      } else {
        setSearchFieldsData({
          ...searchFieldsData,
          [name]: value,
        });
      }
    } else if (isUserAdmin || isUserAdminEdit) {
      const { name, value } = e.target;

      setSearchFieldsData({
        ...searchFieldsData,
        [name]: value,
      });
    }
  };

  function handleColumnPersistence() {
    let columns = [];

    // Checking if key exists in local storage
    if (
      window.localStorage.getItem(
        "adminInspectionsTableColumnsBrandsClient"
      ) === null
    ) {
      let tempColumns = [];
      // If key doesn't exists then store the actual column order in local storage
      // localStorage.setItem(
      // "adminInspectionsTableColumns",
      // JSON.stringify({
      tempColumns.push({
        title: "Inspection Type",
        field: "inspection_type",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
        sorting: false,
      });
      tempColumns.push({
        title: "Inspection Date",
        field: "inspection_date",
        type: "date",
        dateSetting: "en-US",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Cert#",
        field: "certificate_number",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
        sorting: false,
      });
      tempColumns.push({
        title: "Inspector",
        field: "inspector_name",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
        sorting: true,
      });
      tempColumns.push({
        title: "#Cattle",
        field: "number_of_cattle",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "#Elk",
        field: "number_of_elk",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "#Horse",
        field: "number_of_horse",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "#Sheep",
        field: "number_of_sheep",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Beef Promo",
        field: "beef_council_fee",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Predator",
        field: "predator_control_fee",
        searchable: false,
        Cell: (props) => {
          return <> {toCurrency(props.value)} </>;
        },
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Horse Fee",
        field: "horse_inspection_fee",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Cattle Fee",
        field: "cattle_inspection_fee",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Elk Fee",
        field: "elk_inspection_fee",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Sheep Fee",
        field: "sheep_inspection_fee",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Total Fees",
        field: "total_fees",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#fff2cc",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });

      tempColumns.push({
        title: "Owner Name",
        field: "owner_name",
        searchable: true,
        hidden: !displayOwnerName,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          // backgroundColor: "#fff2cc",
          backgroundColor: "#edd2af",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });

      tempColumns.push({
        title: "Buyer Name",
        field: "buyer_name",
        hidden: !displayBuyerName,
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          // backgroundColor: "#fff2cc",
          backgroundColor: "#edd2af",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });

      tempColumns.push({
        title: "Payer Name",
        field: "payer_name",
        hidden: !displayPayerName,
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          // backgroundColor: "#fff2cc",
          backgroundColor: "#edd2af",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });

      tempColumns.push({
        title: "Check#",
        field: "check_number",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#e2efd9",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Check Payee",
        field: "check_payee",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#e2efd9",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Trans#",
        field: "transaction_id",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#e2efd9",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Payment Status",
        field: "payment_status",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#e2efd9",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Payment Date",
        field: "payment_date",
        type: "date",
        dateSetting: "en-US",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#e2efd9",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Payment Type",
        field: "payment_type",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#e2efd9",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Deposit Date",
        field: "deposit_date",
        type: "date",
        dateSetting: "en-US",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#deeaf6",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Reconciled Date",
        field: "reconciled_date",
        type: "date",
        dateSetting: "en-US",
        searchable: false,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#deeaf6",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Reconciled Status",
        field: "reconciled_status",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#deeaf6",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });
      tempColumns.push({
        title: "Reconciled Notes",
        field: "reconcile_note",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#deeaf6",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });

      tempColumns.push({
        title: "Reconciled By",
        field: "reconciled_by_user",
        searchable: true,
        headerStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          backgroundColor: "#deeaf6",
        },
        cellStyle: {
          whiteSpace: "nowrap",
          padding: "5px",
          verticalAlign: "top",
        },
      });

      window.localStorage.setItem(
        "adminInspectionsTableColumnsBrandsClient",
        JSON.stringify(tempColumns)
      );
    }

    let savedColumns = JSON.parse(
      window.localStorage.getItem("adminInspectionsTableColumnsBrandsClient")
    );
    for (var columnIndex in savedColumns) {
      columns.push(savedColumns[columnIndex]);
    }
    return columns;
  }

  const tableRef = useRef();

  const dateIsGreaterThanMinDate = (selectedDt) => {
    let isDateValid = !isNaN(new Date(selectedDt).getTime());
    if (isDateValid) {
      var bits = selectedDt.split("-"); // ex: 2021-12-31

      const selectedYear = bits[0];
      if (selectedYear >= MIN_DATE.getFullYear()) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  const isInsStartDateGreaterThanEndDate = () => {
    const insStartDate = Date.parse(searchFieldsData.inspectionStartDate);
    const insEndDate = Date.parse(searchFieldsData.inspectionEndDate);

    if (insStartDate > insEndDate) return true;

    return false;
  };

  const validateSearchData = () => {
    if (searchFieldsData.inspectionStartDate === "") {
      enqueueSnackbar("Inspection Start Date cannot be empty", {
        variant: "error",
      });
      return false;
    }

    if (searchFieldsData.inspectionEndDate === "") {
      enqueueSnackbar("Inspection End Date cannot be empty", {
        variant: "error",
      });

      return false;
    }

    if (searchFieldsData.inspector === "") {
      enqueueSnackbar("Inspector cannot be empty", {
        variant: "error",
      });
      return false;
    }

    if (!dateIsGreaterThanMinDate(searchFieldsData.inspectionStartDate)) {
      enqueueSnackbar(
        `Inspection Start Date cannot be less than ${MIN_DATE.toLocaleDateString()}`,
        {
          variant: "error",
        }
      );
      return false;
    }

    if (!dateIsGreaterThanMinDate(searchFieldsData.inspectionEndDate)) {
      enqueueSnackbar(
        `Inspection End Date cannot be less than ${MIN_DATE.toLocaleDateString()}`,
        {
          variant: "error",
        }
      );
      return false;
    }

    if (isInsStartDateGreaterThanEndDate()) {
      enqueueSnackbar("Inspection Start Date cannot be greater than end date", {
        variant: "error",
      });
      return false;
    }

    // check for fields removing default value of *
    if (searchFieldsData.certificateNumber.trim() === "") {
      searchFieldsData.certificateNumber = "*";
    }
    if (searchFieldsData.ownerName.trim() === "") {
      searchFieldsData.ownerName = "*";
    }
    if (searchFieldsData.payerName.trim() === "") {
      searchFieldsData.payerName = "*";
    }
    if (searchFieldsData.buyerName.trim() === "") {
      searchFieldsData.buyerName = "*";
    }
    if (searchFieldsData.checkNumber.trim() === "") {
      searchFieldsData.checkNumber = "*";
    }

    if (searchFieldsData.reconcileStatus === "reconciled") {
      if (searchFieldsData.reconcileStartingDate != "") {
        if (isNaN(Date.parse(searchFieldsData.reconcileStartingDate))) {
          enqueueSnackbar("Reconcile starting date is invalid", {
            variant: "error",
          });

          return false;
        }

        if (!isNaN(Date.parse(searchFieldsData.reconcileStartingDate))) {
          // received valid dates
          // check if reconciled date is less than min date

          let bits = searchFieldsData.reconcileStartingDate.split("-");
          let reconcileSelectedYear = bits[0];

          if (reconcileSelectedYear < MIN_DATE.getFullYear()) {
            enqueueSnackbar(
              `Reconcile Start Date cannot be less than ${MIN_DATE.toLocaleDateString()}`,
              {
                variant: "error",
              }
            );

            return false;
          }

          if (searchFieldsData.reconcileEndingDate === "") {
            searchFieldsData.reconcileEndingDate = getCurrentDateAsYYYYMMDD();
          }

          if (searchFieldsData.reconcileEndingDate != "") {
            if (
              Date.parse(searchFieldsData.reconcileStartingDate) >
              Date.parse(searchFieldsData.reconcileEndingDate)
            ) {
              enqueueSnackbar(
                "Reconcile Start Date cannot be greater than reconcile end date",
                {
                  variant: "error",
                }
              );
              return false;
            }
          }

          if (searchFieldsData.reconcileEndingDate != "") {
            if (Date.parse(searchFieldsData.reconcileEndingDate) > Date.now()) {
              enqueueSnackbar(
                "Reconcile Ending Date cannot be greater than current end date",
                {
                  variant: "error",
                }
              );

              return false;
            }
          }
        } else {
          // Not a valid number and not empty so incorrect date
          enqueueSnackbar("Reconcile Starting date is invalid!", {
            variant: "error",
          });

          return false;
        }
      }
    } else {
      searchFieldsData.reconcileStartingDate = "";
      searchFieldsData.reconcileEndingDate = "";
    }

    return true;
  };

  const getQueryString = () => {
    const querystring = require("querystring");
    let queryString = querystring.stringify(searchFieldsData);
    return queryString;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      let baseUrl = `${inspectionDataURL}`;

      let fetchedInspectionData = [];
      let timeStampToken = null;
      let totalPages = 0;
      let totalItems = 0;
      let pageNumber = 1;
      const defaultFetchPageSize = 100;

      let firstCallResponse = await createNetInspectionApiClient().get(
        `${baseUrl}&${getQueryString()}&PageNumber=${pageNumber}&PageSize=${defaultFetchPageSize}`
      );

      // extract data
      timeStampToken = firstCallResponse.data.time_stamp_token;
      totalPages = firstCallResponse.data.paging.total_pages;
      totalItems = firstCallResponse.data.paging.total_items;
      // get the first page's data
      fetchedInspectionData.push(...firstCallResponse.data.items);

      // loop and request for other page data
      for (let i = 2; i <= totalPages; i++) {
        const fetchedData = await createNetInspectionApiClient().get(
          `${baseUrl}&${getQueryString()}&PageNumber=${i}&PageSize=${defaultFetchPageSize}&TimeStamp=${timeStampToken}`
        );
        fetchedInspectionData.push(...fetchedData.data.items);
      }
      // once we have all data retrieved
      // set the local state data
      setInspectionData(fetchedInspectionData);
      // console.log(fetchedInspectionData);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      enqueueSnackbar(
        (err && err.message) || "Oops! Something went wrong. Please try again!",
        { variant: "error" }
      );
    } finally {
    }
  };

  const handleFormSubmit = async () => {
    try {
      if (validateSearchData()) {
        // first clear the data
        setInspectionData([]);

        loadData();
        if (isUserAdminEdit) {
          setDisableReconcileButton(false);
        } else {
          setDisableReconcileButton(true);
        }
      } else {
        setInspectionData([]);
      }
    } catch (error) {
    } finally {
    }
  };

  // Code to convert data to excel
  const exportToExcel = () => {
    if (inspectionData.length > 0) {
      const newData = inspectionData.map((row) => {
        delete row.tableData;
        return row;
      });
      const workSheet = XLSX.utils.json_to_sheet(newData);
      const workBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workBook, workSheet, "Inspections");
      // Buffer to handle bulk data
      let buffer = XLSX.write(workBook, { bookType: "xlsx", type: "buffer" });
      // generate binary string
      XLSX.write(workBook, { bookType: "xlsx", type: "binary" });
      //download file
      XLSX.writeFile(workBook, "InspectionsData.xlsx");
    }
  };

  function toCurrency(numberString) {
    let number = parseFloat(numberString);
    return number.toLocaleString("USD");
  }

  function handleColumnDrag(sourceIndex, destinationIndex) {
    let columns = [];
    let tempColumns = [];
    let savedColumns = JSON.parse(
      window.localStorage.getItem("adminInspectionsTableColumnsBrandsClient")
    );
    // it is important to check validity of savedColumns as it could be deleted by user.
    if (savedColumns) {
      const sourceColumn = savedColumns[sourceIndex];

      for (var columnIndex in savedColumns) {
        if (columnIndex == sourceIndex) {
          // do nothing
        } else {
          tempColumns.push(savedColumns[columnIndex]);
        }
      }

      let newArray = [
        ...tempColumns.slice(0, destinationIndex),
        sourceColumn,
        ...tempColumns.slice(destinationIndex),
      ];

      window.localStorage.setItem(
        "adminInspectionsTableColumnsBrandsClient",
        JSON.stringify(newArray)
      );

      for (var columnIndex in newArray) {
        columns.push(newArray[columnIndex]);
      }

      setTableColumns(columns);
    }
  }

  let content;

  if (loading) {
    content = <LoadingIndicator />;
  } else {
    content = (
      <>
        <div>
          <MaterialTable
            title="Inspections"
            columns={tableColumns}
            data={inspectionData}
            options={{
              headerStyle: { position: "sticky" },
              pageSize: 10,
              search: true,
              selection: true,
              selectionProps: (rowData) => ({
                // disabled: rowData.reconciled_status !== "unreconciled",
              }),
              pageSizeOptions: [10, 50, 100],
              paging: true,
              toolbar: true,
              maxBodyHeight: 450,
              doubleHorizontalScroll: true,
              // This controls the row height
              padding: "dense",
            }}
            localization={{
              header: {
                actions: "View History",
              },
            }}
            onSelectionChange={(rows) => {
              let reconcileCount = 0;
              rows.map((row) => {
                if (row.reconciled_status === "reconciled") {
                  reconcileCount++;
                }
              });

              if (reconcileCount > 0) {
                setDisableEditPaymentButton(true);
                setDisableReconcileButton(true);
                if (isUserAdminEdit) {
                  setDisableUnreconcileButton(false);
                } else {
                  setDisableUnreconcileButton(true);
                }
              } else {
                if (rows.length >= 1 && isUserAdminEdit) {
                  setDisableEditPaymentButton(false);
                  setDisableReconcileButton(false);
                  setDisableUnreconcileButton(true);
                } else {
                  setDisableEditPaymentButton(true);
                  setDisableUnreconcileButton(true);
                  setDisableReconcileButton(true);
                }
              }
              setSelectedRows(rows);
            }}
            onColumnDragged={(sourceIndex, destinationIndex) =>
              handleColumnDrag(sourceIndex, destinationIndex)
            }
            actions={[
              // (rowData) => {
              //   return {
              //     disabled: rowData.reconciled_status === "unreconciled",
              //   };
              // },
              {
                icon: () => <GetAppIcon>Export to Excel</GetAppIcon>,
                tooltip: "Export to Excel",
                isFreeAction: true,
                onClick: () => {
                  exportToExcel();
                  handleFormSubmit();
                },
              },
              {
                icon: () => <ViewListOutlinedIcon />,
                tooltip: "View Reconcile History",
                position: "row",
                // hidden: (rowData) => {
                //   return rowData.reconciled_status === "reconciled";
                // },
                // onClick: (event, rowData) => alert("You saved " + rowData.name),
                onClick: (event, rowData) => {
                  if (rowData && rowData.reconciled_status === "reconciled") {
                    setRowDataForReconcileHistory(rowData);
                    setOpenPopupReconcileHistory(true);
                  }
                },
              },
              {
                icon: () => <Receipt />,
                tooltip: "View Payment Log",
                position: "row",
                // hidden: (rowData) => {
                //   return rowData.reconciled_status === "reconciled";
                // },
                // onClick: (event, rowData) => alert("You saved " + rowData.name),
                onClick: (event, rowData) => {
                  if (
                    rowData &&
                    rowData.payment_id !== null &&
                    rowData.payment_id > 0
                  ) {
                    setPaymentIdForViewPaymentLog(rowData.payment_id);
                    setOpenPopupViewPaymentLog(true);
                  }
                },
              },
            ]}
            components={{
              Pagination: PatchedPagination,
            }}
            tableRef={tableRef}
          />
        </div>
      </>
    );
  }

  const getRowDataForReconcileHistory = () => {
    return { ...rowDataForReconcileHistory };
  };

  return (
    <>
      <Typography variant="h5" style={{ marginBottom: "20px" }}>
        Reconcilliation Search
      </Typography>

      {/* form begin */}
      <form
        style={{ width: "700px" }}
        onSubmit={(event) => {
          event.preventDefault();
          handleFormSubmit();
        }}
      >
        {/* Inspection section begin */}
        <Typography variant="h6" className={classes.headerStyle}>
          Inspection
        </Typography>
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          style={{ marginLeft: "10px" }}
        >
          <Grid item>
            <TextField
              label="Start Dt"
              name="inspectionStartDate"
              id="inspectionStartDate"
              type="date"
              // defaultValue="mm/dd/yyyy"
              fontSize="12"
              margin="none"
              required={true}
              inputProps={{
                min: "2022-01-01",
                style: { fontSize: 12, margin: 0 },
              }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: 14, margin: 8 },
              }}
              padding="0px 0px 0px 0px"
              className={classes.dateTextFieldField}
              value={searchFieldsData.inspectionStartDate}
              onChange={(e) => {
                handleInputChange(e);
              }}
            />
            <TextField
              label="End Dt"
              name="inspectionEndDate"
              id="inspectionEndDate"
              type="date"
              // defaultValue="mm/dd/yyyy"
              fontSize="12"
              margin="none"
              required={true}
              inputProps={{
                min: "2022-01-01",
                style: { fontSize: 12, margin: 0 },
              }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: 14, margin: 8 },
              }}
              padding="0px 0px 0px 0px"
              className={classes.dateTextFieldField}
              value={searchFieldsData.inspectionEndDate}
              onChange={(e) => {
                handleInputChange(e);
              }}
            />
          </Grid>
          <Grid item>
            {/* Inspector list begin */}
            <FormControl
              required={true}
              className={classes.formControl}
              style={{ width: 200 }}
            >
              <InputLabel
                shrink
                id="demo-simple-select-placeholder-label-label"
              >
                Inspector
              </InputLabel>
              <Select
                labelId="demo-simple-select-placeholder-label-label"
                name="inspector"
                id="demo-simple-select-placeholder-label"
                required={true}
                value={searchFieldsData.inspector}
                onChange={(e) => {
                  handleInputChange(e);
                }}
                displayEmpty
                className={classes.selectEmpty}
              >
                {(isUserAdmin || isUserAdminEdit) && (
                  <MenuItem value="all">
                    <em>All</em>
                  </MenuItem>
                )}
                {(isUserAdmin || isUserAdminEdit) &&
                  getInspectorList().map((item, index) => {
                    return (
                      <MenuItem key={index} value={item.title}>
                        {item.title}
                      </MenuItem>
                    );
                  })}
                {isUserInspector && (!isUserAdmin || !isUserAdminEdit) && (
                  <MenuItem value={user.name}>
                    <em>{user.name}</em>
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            {/* Inspector list end */}
          </Grid>
          <Grid item>
            <Typography className={classes.labelHeight}>
              Certificate Nbr
            </Typography>
            <TextField
              // id={id}
              name="certificateNumber"
              id="certificate_number"
              variant="outlined"
              value={searchFieldsData.certificateNumber}
              // maxLength={20}
              onChange={(e) => {
                handleInputChange(e);
              }}
              size="small"
              className={classes.textField}
              inputProps={{
                maxLength: 22,
                // labelHeight: 20,
              }}
              InputLabelProps={{
                // shrink: true,

                style: {
                  fontSize: 12,
                  margin: 4,
                  padding: "4px 4px",
                  maxLength: 22,
                },
              }}
            />
          </Grid>
        </Grid>
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          style={{ marginLeft: "10px" }}
        >
          <Grid item>
            <Typography className={classes.labelHeight}>Owner Name</Typography>
            <TextField
              // id={id}
              name="ownerName"
              id="owner_name"
              variant="outlined"
              value={searchFieldsData.ownerName}
              onChange={(e) => {
                handleInputChange(e);
              }}
              size="small"
              className={classes.textField}
              style={{ marginRight: "20px" }}
              inputProps={{
                maxLength: 60,
                // labelHeight: 20,
              }}
              InputLabelProps={{
                shrink: true,
                style: {
                  fontSize: 14,
                  margin: 8,
                },
              }}
            />
          </Grid>
          <Grid item>
            <Typography className={classes.labelHeight}>Buyer Name</Typography>
            <TextField
              // id={id}
              name="buyerName"
              id="buyer_name"
              variant="outlined"
              value={searchFieldsData.buyerName}
              onChange={(e) => {
                handleInputChange(e);
              }}
              size="small"
              className={classes.textField}
              style={{ marginRight: "8px" }}
              inputProps={{
                maxLength: 60,
                // labelHeight: 20,
              }}
              InputLabelProps={{
                shrink: true,
                style: {
                  fontSize: 14,
                  margin: 8,
                },
              }}
            />
          </Grid>
          <Grid item>
            <Typography className={classes.labelHeight}>Payer Name</Typography>
            <TextField
              // id={id}
              name="payerName"
              id="payer_name"
              variant="outlined"
              value={searchFieldsData.payerName}
              onChange={(e) => {
                handleInputChange(e);
              }}
              size="small"
              className={classes.textField}
              style={{ marginRight: "0px" }}
              inputProps={{
                maxLength: 60,
                // labelHeight: 20,
              }}
              InputLabelProps={{
                shrink: true,
                style: {
                  fontSize: 14,
                  margin: 8,
                },
              }}
            />
          </Grid>
        </Grid>
        {/* Inspection section end */}
        {/* Payment section begin */}
        <Typography variant="h6" className={classes.headerStyle}>
          Payment
        </Typography>
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          style={{ marginLeft: "10px" }}
        >
          <Grid item>
            {/* Payment Status begin */}
            <FormControl className={classes.formControl} style={{ width: 200 }}>
              <InputLabel
                shrink
                id="demo-simple-select-placeholder-label-label"
              >
                Payment Status
              </InputLabel>
              <Select
                labelId="demo-simple-select-placeholder-label-label"
                name="paymentStatus"
                id="demo-simple-select-placeholder-label"
                value={searchFieldsData.paymentStatus}
                onChange={(e) => {
                  handleInputChange(e);
                }}
                displayEmpty
                className={classes.selectEmpty}
              >
                <MenuItem value="all">
                  <em>All</em>
                </MenuItem>
                {getPaymentStatusList().map((item, index) => {
                  return (
                    <MenuItem key={index} value={item.title}>
                      {item.title}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {/* Payment Status end */}
          </Grid>
          <Grid item>
            {/* Payment type begin */}
            <FormControl className={classes.formControl} style={{ width: 200 }}>
              <InputLabel
                shrink
                id="demo-simple-select-placeholder-label-label"
              >
                Payment Type
              </InputLabel>
              <Select
                labelId="demo-simple-select-placeholder-label-label"
                name="paymentType"
                id="demo-simple-select-placeholder-label"
                value={searchFieldsData.paymentType}
                onChange={(e) => {
                  handleInputChange(e);
                }}
                displayEmpty
                className={classes.selectEmpty}
              >
                <MenuItem value="all">
                  <em>All</em>
                </MenuItem>
                {getPaymentTypeList().map((item, index) => {
                  return (
                    <MenuItem key={index} value={item.id}>
                      {item.title}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {/* Payment Type end */}
          </Grid>
          <Grid item>
            <Typography className={classes.labelHeight}>Check Nbr</Typography>
            <TextField
              // id={id}
              name="checkNumber"
              id="check_number"
              variant="outlined"
              value={searchFieldsData.checkNumber}
              onChange={(e) => {
                handleInputChange(e);
              }}
              size="small"
              className={classes.textField}
              inputProps={{
                maxLength: 60,
                // labelHeight: 20,
              }}
              InputLabelProps={{
                shrink: true,
                style: {
                  fontSize: 14,
                  margin: 8,
                },
              }}
            />
          </Grid>
        </Grid>
        {/* Payment section end */}
        {/* Reconcile section begin */}
        <Typography variant="h6" className={classes.headerStyle}>
          Reconcilliation
        </Typography>
        {/* Reconcile status begin  */}
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          style={{ marginLeft: "10px" }}
        >
          <Grid item>
            <FormControl component="fieldset">
              {/* <FormLabel component="legend">Reconcilliation </FormLabel> */}
              <RadioGroup
                aria-label="reconcileStatus"
                name="reconcileStatus"
                value={searchFieldsData.reconcileStatus}
                onChange={(e) => {
                  handleInputChange(e);
                }}
                row
              >
                <FormControlLabel
                  value="reconciled"
                  control={<Radio />}
                  label="Reconciled"
                />
                <FormControlLabel
                  value="unreconciled"
                  control={<Radio />}
                  label="Unreconciled"
                />
                <FormControlLabel value="all" control={<Radio />} label="All" />
              </RadioGroup>
            </FormControl>
            {/* Reconcile status end  */}
          </Grid>
          <Grid item>
            <TextField
              label="Start Dt"
              name="reconcileStartingDate"
              id="reconcileStartingDate"
              type="date"
              // defaultValue="mm/dd/yyyy"
              fontSize="12"
              margin="none"
              inputProps={{
                min: "2022-01-01",
                style: { fontSize: 12, margin: 0 },
              }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: 14, margin: 8 },
              }}
              padding="0px 0px 0px 0px"
              className={classes.dateTextFieldField}
              value={searchFieldsData.reconcileStartingDate}
              onChange={(e) => {
                handleInputChange(e);
              }}
            />
            <TextField
              label="End Dt"
              name="reconcileEndingDate"
              id="reconcileEndingDate"
              type="date"
              // defaultValue="mm/dd/yyyy"
              fontSize="12"
              margin="none"
              inputProps={{
                min: "2022-01-01",
                style: { fontSize: 12, margin: 0 },
              }}
              InputLabelProps={{
                shrink: true,
                style: { fontSize: 14, margin: 8 },
              }}
              padding="0px 0px 0px 0px"
              className={classes.dateTextFieldField}
              value={searchFieldsData.reconcileEndingDate}
              onChange={(e) => {
                handleInputChange(e);
              }}
            />
          </Grid>
        </Grid>
        {/* Reconcile section end  */}

        {/* Display and buttons  begin */}
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          style={{ marginLeft: "10px" }}
        >
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              style={{
                width: "80px",
                height: "20px",
                fontSize: "0.8rem",
                padding: "0px",
              }}
              onClick={(e) => {
                handleFormSubmit(e);
              }}
            >
              Search
            </Button>
            <Button
              variant="contained"
              style={{
                width: "100px",
                height: "20px",
                fontSize: "0.8rem",
                padding: "0px",
              }}
              onClick={() => {
                exportToExcel();
                handleFormSubmit();
              }}
            >
              Excel Export
            </Button>
            <Button
              variant="contained"
              style={{
                width: "100px",
                height: "20px",
                fontSize: "0.8rem",
                padding: "0px",
                color: "#000",
                backgroundColor: "#fff",
              }}
              onClick={() => {
                setSearchFieldsData(initialFieldValues);
                setInspectionData([]);
              }}
            >
              Clear
            </Button>
          </Grid>
          <Grid item>
            <FormControl component="fieldset" className={classes.formControl}>
              {/* <FormLabel component="legend" >
                Display
              </FormLabel> */}
              <FormGroup row>
                <FormLabel
                  component="legend"
                  style={{
                    paddingTop: "15px",
                    paddingRight: "4px",
                    fontSize: "0.8rem",
                  }}
                >
                  Display
                </FormLabel>
                <FormControlLabel
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: "0.8rem",
                  }}
                  control={
                    <Checkbox
                      checked={displayOwnerName}
                      onChange={handleDisplayOptionsChange}
                      name="displayOwnerName"
                      className={classes.checkBox}
                    />
                  }
                  label="Owner Name"
                  className={classes.checkBox}
                />
                <FormControlLabel
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: "0.8rem",
                  }}
                  control={
                    <Checkbox
                      checked={displayBuyerName}
                      onChange={handleDisplayOptionsChange}
                      name="displayBuyerName"
                      className={classes.checkBox}
                    />
                  }
                  label="Buyer Name"
                  className={classes.checkBox}
                />
                <FormControlLabel
                  style={{
                    margin: 0,
                    padding: 0,
                    fontSize: "0.8rem",
                  }}
                  control={
                    <Checkbox
                      checked={displayPayerName}
                      onChange={handleDisplayOptionsChange}
                      name="displayPayerName"
                      className={classes.checkBox}
                    />
                  }
                  label="Payer Name"
                  className={classes.checkBox}
                />
              </FormGroup>
            </FormControl>
          </Grid>
        </Grid>

        {/* Display and buttons end */}
      </form>
      <>
        <Grid
          container
          spacing={2}
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          style={{ marginLeft: "10px" }}
        >
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              // disabled={selectedRows && selectedRows.length > 1 ? true : false}
              disabled={disableEditPaymentButton}
              style={{
                width: "80px",
                height: "20px",
                fontSize: "0.6rem",
                padding: "0px",
              }}
              onClick={(e) => {
                setOpenPopupEditPayment(true);
              }}
            >
              Edit Payment
            </Button>
            <Button
              disabled={disableReconcileButton}
              variant="contained"
              style={{
                width: "100px",
                height: "20px",
                fontSize: "0.6rem",
                padding: "0px",
              }}
              onClick={(e) => {
                if (selectedRows && selectedRows.length > 0) {
                  setOpenPopupReconcileSubmit(true);
                } else {
                  enqueueSnackbar(
                    "Records must be selected for reconcillation!",
                    { variant: "error" }
                  );
                }
              }}
            >
              Reconcile
            </Button>
            <Button
              disabled={disableUnreconcileButton}
              variant="contained"
              style={{
                width: "100px",
                height: "20px",
                fontSize: "0.6rem",
                padding: "0px",
              }}
              onClick={(e) => {
                if (selectedRows && selectedRows.length > 0) {
                  setOpenPopupUnreconcileSubmit(true);
                } else {
                  enqueueSnackbar(
                    "Records must be selected for Unreconcillation!",
                    { variant: "error" }
                  );
                }
              }}
            >
              Unreconcile
            </Button>
          </Grid>
        </Grid>
      </>
      <br />
      <>{content}</>

      <>
        {openPopupReconcileSubmit && (
          <Popup
            title="Reconcile"
            openPopup={openPopupReconcileSubmit}
            setOpenPopup={setOpenPopupReconcileSubmit}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          >
            <ReconcileSubmit
              // title="Reconcile"
              openPopup={openPopupReconcileSubmit}
              setOpenPopup={setOpenPopupReconcileSubmit}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              reconciledBy={user.name}
              reconcileSubmitDataUrl={getAdminInspectionsReconcileApiUrl}
              handleSearchFormSubmit={handleFormSubmit}
              createNetInspectionApiClient={createNetInspectionApiClient}
              minDate={MIN_DATE}
              dateIsGreaterThanMinDate={dateIsGreaterThanMinDate}
              setDisableEditPaymentBtn={setDisableEditPaymentButton}
              setDisableReconcileButton={setDisableReconcileButton}
              setDisableUnreconcileButton={setDisableUnreconcileButton}
            />
          </Popup>
        )}
      </>
      <>
        {openPopupUnreconcileSubmit && (
          <Popup
            title="Unreconcile"
            openPopup={openPopupUnreconcileSubmit}
            setOpenPopup={setOpenPopupUnreconcileSubmit}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
          >
            <UnreconcileSubmit
              // title="Reconcile"
              openPopup={openPopupUnreconcileSubmit}
              setOpenPopup={setOpenPopupUnreconcileSubmit}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              reconciledBy={user.name}
              unreconcileSubmitDataUrl={getAdminInspectionsUnreconcileApiUrl}
              handleSearchFormSubmit={handleFormSubmit}
              createNetInspectionApiClient={createNetInspectionApiClient}
              minDate={MIN_DATE}
              dateIsGreaterThanMinDate={dateIsGreaterThanMinDate}
              setDisableEditPaymentBtn={setDisableEditPaymentButton}
              setDisableReconcileButton={setDisableReconcileButton}
              setDisableUnreconcileButton={setDisableUnreconcileButton}
            />
          </Popup>
        )}
      </>
      <>
        {openPopupReconcileHistory && (
          <Popup
            title="Reconcile History"
            openPopup={openPopupReconcileHistory}
            setOpenPopup={setOpenPopupReconcileHistory}
          >
            <ViewReconcileHistory
              openPopup={openPopupReconcileHistory}
              setOpenPopup={setOpenPopupReconcileHistory}
              reconcileHistoryData={getRowDataForReconcileHistory()}
            />
          </Popup>
        )}
      </>
      <>
        {openPopupEditPayment && (
          <Popup
            title="Edit Payment"
            openPopup={openPopupEditPayment}
            setOpenPopup={setOpenPopupEditPayment}
          >
            <EditPayment
              openPopup={openPopupEditPayment}
              setOpenPopup={setOpenPopupEditPayment}
              selectedRows={selectedRows}
              reconciledBy={user.name}
              editPaymentApiUrl={getAdminEditPaymentApiUrl}
              handleSearchFormSubmit={handleFormSubmit}
              disableEditPaymentBtn={setDisableEditPaymentButton}
              createNetInspectionApiClient={createNetInspectionApiClient}
            />
          </Popup>
        )}
      </>
      <>
        {openPopupViewPaymentLog && (
          <Popup
            title="Payment History"
            openPopup={openPopupViewPaymentLog}
            setOpenPopup={setOpenPopupViewPaymentLog}
          >
            <ViewPaymentLog
              openPopup={openPopupViewPaymentLog}
              setOpenPopup={setOpenPopupViewPaymentLog}
              paymentLogApiUrl={getAdminInspectionsPaymentLogApiUrl}
              paymentIdForViewPaymentLog={paymentIdForViewPaymentLog}
              createNetInspectionApiClient={createNetInspectionApiClient}
            />
          </Popup>
        )}
      </>
    </>
  );
};

export default SearchInspections;
